import type { ClaimLinkResponse } from "@solomon-apps/shared";
import { ContentBlock } from "@solomon-apps/shared/src/components/console";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import {
    Button,
    Card,
    CardContent,
    Copy,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";

import { LabeledInput } from "@/pages/shared/LabeledField";
import { OUTLINE_GROUP_CLASS } from "@/pages/shared/lightbarField";

/**
 * The button's outcome states. `failed` holds briefly, then settles to
 * `retry`; `sent` / `ready` hold briefly, then settle to `again`.
 */
export type ResetPhase =
    | "idle"
    | "sending"
    | "sent"
    | "ready"
    | "again"
    | "failed"
    | "retry";

const RESET_LABELS: Record<ResetPhase, string> = {
    idle: "Send Reset Link",
    sending: "Sending...",
    // Emailed to the member, and shown below to copy.
    sent: "Link Sent",
    // Email delivery was unavailable: the link is below, to send by hand.
    ready: "Link Ready",
    again: "Send New Link",
    failed: "Not Sent",
    retry: "Retry"
};

/** How long an outcome label holds before settling (ms). */
const OUTCOME_HOLD_MS = 2000;

interface ResetPasswordCardProps {
    /**
     * Why the reset is unavailable; null = available. A locked reset is
     * simply disabled: no tooltip on this button (user call).
     */
    lockReason: string | null;
    /** Issues the reset link (`POST .../:uuid/reset-password`). */
    onSend: () => Promise<ClaimLinkResponse>;
    /** Reads a failure as the console words it. */
    describeError: (error: unknown) => string;
}

/**
 * The member page's right column: an Owner resets a teammate's password on
 * their behalf. Admins cannot set another member's password, so this issues
 * a one-time reset LINK (emailed when delivery is available) and shows it in
 * a read-only field to copy. Same shell as Profile's Change Password: a
 * sunken, outlined card holding outline fields.
 *
 * The Send Reset Link button carries the outcome (Send Reset Link, Sending..., Link Sent or
 * Not Sent, then Send New Link or Retry); a failure's reason is on demand
 * on the button, never a sentence in the card.
 */
export function ResetPasswordCard({
    lockReason,
    onSend,
    describeError
}: ResetPasswordCardProps): React.ReactElement {
    const [phase, setPhase] = useState<ResetPhase>("idle");
    const [url, setUrl] = useState<string | null>(null);
    const [failure, setFailure] = useState<string | null>(null);
    const holdTimer = useRef<number | null>(null);
    const reducedMotion = useReducedMotion() ?? false;

    useEffect(
        () => (): void => {
            if (holdTimer.current !== null) {
                window.clearTimeout(holdTimer.current);
            }
        },
        []
    );

    const settle = (to: ResetPhase): void => {
        if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
        holdTimer.current = window.setTimeout(() => {
            holdTimer.current = null;
            setPhase(to);
        }, OUTCOME_HOLD_MS);
    };

    const send = async (): Promise<void> => {
        if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
        setPhase("sending");
        setFailure(null);
        try {
            const result = await onSend();
            setUrl(result.claimUrl);
            setPhase(result.delivery === "email_and_url" ? "sent" : "ready");
            settle("again");
        } catch (caught) {
            setFailure(describeError(caught));
            setPhase("failed");
            settle("retry");
        }
    };

    const button = (
        <Button
            // Sunken + outline + lifted (as Add a Position's Back): the
            // secondary action; Suspend is the page's one raised action.
            variant="sunken"
            outline
            interaction="lifted"
            size="md"
            onClick={() => void send()}
            disabled={lockReason !== null || phase === "sending"}
            labels={RESET_LABELS}
            labelKey={phase}
            aria-label={
                failure ? `${RESET_LABELS[phase]}: ${failure}` : undefined
            }
        />
    );

    return (
        <Card surface="sunken" border="line">
            <CardContent className="pt-6">
                <ContentBlock title="Reset Password" className="gap-1 md:gap-2">
                    {url && (
                        // The link arrives with a fade (opacity only, 200ms
                        // ease-out-quad), skipped under reduced motion.
                        <motion.div
                            key={url}
                            initial={reducedMotion ? false : { opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{
                                duration: 0.2,
                                ease: easings.outQuad
                            }}
                            className="flex flex-col gap-3 py-4"
                        >
                            <Copy
                                content={url}
                                copyLabel="Copy reset link"
                                copiedLabel="Copied"
                                className="flex w-full"
                            >
                                <LabeledInput
                                    label="Reset Link"
                                    groupClassName={OUTLINE_GROUP_CLASS}
                                    type="text"
                                    value={url}
                                    readOnly
                                    aria-label="Reset Link"
                                    onFocus={(e) => e.currentTarget.select()}
                                />
                            </Copy>
                        </motion.div>
                    )}
                    {/* One tree for every phase, so the label swap cross-fades
                        instead of remounting; the tooltip only opens while
                        there is a failure. */}
                    <div className="flex items-center justify-start py-3">
                        <TooltipProvider>
                            <Tooltip disabled={!failure}>
                                <TooltipTrigger render={button} />
                                <TooltipContent side="top">
                                    {failure}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </ContentBlock>
            </CardContent>
        </Card>
    );
}
